import {FlexColumn, Modal, SubHeading, Table} from '@forecast-it/design-system';
import moment from 'moment';
import React from 'react';
import {useIntl} from 'react-intl';
import {currencies} from '../../../constants';
import {MODAL_TYPE, showModal} from '../../shared/components/modals/generic_modal_conductor';
import type {ModalProps} from '../../shared/components/modals/types';
import Util from '../../shared/util/util';
import type {ExchangeRate} from './types';

export type CurrencyHistoryModalPayload = {
	currencyCode: string;
	exchangeRates: ExchangeRate[];
};

export const showCurrencyHistoryModal = (props: CurrencyHistoryModalPayload): void => {
	showModal({
		type: MODAL_TYPE.CURRENCY_HISTORY,
		...props,
	});
};

type CurrencyHistoryModalProps = ModalProps<CurrencyHistoryModalPayload>;

type RateStatus = 'current' | 'past';

const STATUS_COLORS: Record<RateStatus, string> = {
	current: '#d9f7ec',
	past: '#F5F3F9',
};

export const CurrencyHistoryModal = ({currencyCode, exchangeRates, closeModal}: CurrencyHistoryModalProps) => {
	const {formatMessage} = useIntl();

	const currencyEntry = currencies.find(c => c.code === currencyCode);
	const currencyLabel = currencyEntry
		? `${currencyEntry.code} (${currencyEntry.symbol}) - ${currencyEntry.name}`
		: currencyCode;

	const today = moment();

	const sorted = [...exchangeRates].sort((a, b) => {
		if (a.startDate === null) return -1;
		if (b.startDate === null) return 1;
		return a.startDate.localeCompare(b.startDate);
	});

	const currentIndex = sorted.reduce((found, r, i) => {
		const isCurrent = r.startDate === null || !moment(r.startDate).isAfter(today, 'day');
		return isCurrent ? i : found;
	}, -1);

	const historyRows = sorted.map((rate, i) => {
		const startLabel =
			rate.startDate === null
				? formatMessage({id: 'settings_finance.currencies.history_modal.initial'})
				: moment.utc(rate.startDate).format('DD MMM YYYY');

		const nextStartDate = i < sorted.length - 1 ? sorted[i + 1].startDate : null;
		const endLabel =
			nextStartDate !== null
				? moment.utc(nextStartDate).subtract(1, 'day').format('DD MMM YYYY')
				: formatMessage({id: 'settings_finance.currencies.history_modal.onwards'});

		const status: RateStatus = i === currentIndex ? 'current' : 'past';

		const thisRate = rate.rate ?? 0;

		return {
			id: rate.id,
			effectiveFromDisplay: '',
			startLabel,
			endLabel,
			rateDisplay: '',
			thisRate,
			rateCurrency: rate.currency,
			createdAtFormatted: Util.formatUtcDateTimeToLocalTimezone(rate.createdAt),
			status,
			statusLabel: formatMessage({id: `settings_finance.currencies.history_modal.status_${status}`}),
		};
	});

	return (
		<Modal size={Modal.SIZE.LARGE} onCloseModal={closeModal}>
			<Modal.Title>{formatMessage({id: 'settings_finance.currencies.history_modal.title'})}</Modal.Title>
			<Modal.Content>
				<FlexColumn gap="m">
					<SubHeading size={1}>{currencyLabel}</SubHeading>
					<Table width="100%" data={historyRows}>
						<Table.TextColumn
							accessorKey="effectiveFromDisplay"
							header={formatMessage({id: 'settings_finance.currencies.history_modal.effective_from'})}
							icon={(row: {startLabel: string; endLabel: string}) => (
								<>
									<SubHeading size={2}>{row.startLabel}</SubHeading>
									{` - ${row.endLabel}`}
								</>
							)}
						/>
						<Table.TextColumn
							accessorKey="rateDisplay"
							header={formatMessage({id: 'common.rate'})}
							icon={(row: {thisRate: number; rateCurrency: string}) => (
								<SubHeading size={2}>{`${row.thisRate} ${row.rateCurrency}`}</SubHeading>
							)}
						/>
						<Table.TextColumn
							accessorKey="createdAtFormatted"
							header={formatMessage({id: 'global_filter_field.created_at'})}
						/>
						<Table.ColoredIdIconColumn
							accessorKey="statusLabel"
							header={formatMessage({id: 'settings_finance.currencies.history_modal.status'})}
							renderProps={(row: {status: RateStatus; statusLabel: string}) => ({
								color: STATUS_COLORS[row.status],
								text: row.statusLabel,
							})}
						/>
					</Table>
				</FlexColumn>
			</Modal.Content>
		</Modal>
	);
};
